import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useCallback } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText'; // @ts-expect-error untyped import

import localeComparator from 'I18n/utils/localeComparator';
import { EERIE, CANDY_APPLE } from 'HubStyleTokens/colors';
import UIForm from 'UIComponents/form/UIForm';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UICharacterCounter from 'UIComponents/counters/UICharacterCounter';
import UITextInput from 'UIComponents/input/UITextInput';
import UITextArea from 'UIComponents/input/UITextArea';
import UISelect from 'UIComponents/input/UISelect';
import H4 from 'UIComponents/elements/headings/H4';
import H5 from 'UIComponents/elements/headings/H5';
import ReviewWizardGrid from './ReviewWizardGrid';
import RatingInput from '../RatingInput';
import { STAR_RATING } from '../../constants/RatingTypes';
import * as ReviewQuestions from '../../constants/ReviewQuestions';
import { RatingAxes } from '../../constants/RatingAxes';
import { REVIEW_TITLE_LIMIT, REVIEW_BODY_MIN, REVIEW_BODY_MAX } from '../../constants/ReviewLimits';
const SERVICES_LIMIT = 10;

const getServiceOptions = (numSelected, catalogServices) => catalogServices.map(service => {
  return {
    text: service.name,
    value: service.id,
    disabled: numSelected >= SERVICES_LIMIT
  };
}).sort((a, b) => localeComparator(a.text, b.text));

export default function ProfileRateWizardStep({
  catalogServices,
  reviewableName: companyName,
  onUpdate,
  review
}) {
  const {
    ratings,
    content
  } = review;
  const onAnswerChange = useCallback((question, answer) => {
    onUpdate({
      content: {
        [question]: answer
      }
    });
  }, [onUpdate]);
  const ratingField = useMemo(() => /*#__PURE__*/_jsx(UIFormControl, {
    required: true,
    className: "m-bottom-8",
    label: /*#__PURE__*/_jsx(H5, {
      className: "display-inline-block",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-ui-reviews.wizard.profile.rate.overall"
      })
    }),
    children: /*#__PURE__*/_jsx(RatingInput, {
      "data-test-id": `${RatingAxes.OVERALL}-rating`,
      rating: ratings.get(RatingAxes.OVERALL) || 0,
      onChange: ({
        target: {
          value
        }
      }) => onUpdate({
        ratings: {
          [RatingAxes.OVERALL]: value
        }
      }),
      iconSize: "sm",
      labelSize: "md",
      type: STAR_RATING
    })
  }), [ratings, onUpdate]);
  const services = content.get(ReviewQuestions.SERVICES);
  const servicesSelect = useMemo(() => /*#__PURE__*/_jsx(UIFormControl, {
    required: true,
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "marketplace-ui-reviews.wizard.profile.rate.services.label"
    }),
    children: /*#__PURE__*/_jsx(UISelect, {
      className: "wizard-services-select",
      multi: true,
      onChange: ({
        target: {
          value
        }
      }) => onAnswerChange(ReviewQuestions.SERVICES, value),
      options: getServiceOptions(content.get(ReviewQuestions.SERVICES) ? content.get(ReviewQuestions.SERVICES).length : 0, catalogServices),
      placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-ui-reviews.wizard.profile.rate.services.placeholder"
      }),
      value: services && services
    })
  }), [content, catalogServices, services, onAnswerChange]);
  const reviewTitle = content.get(ReviewQuestions.TITLE);
  const titleField = useMemo(() => /*#__PURE__*/_jsx(UIFormControl, {
    required: true,
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `marketplace-ui-reviews.wizard.profile.rate.labels.${ReviewQuestions.TITLE}`
    }),
    children: /*#__PURE__*/_jsx(UICharacterCounter, {
      counter: "down",
      max: REVIEW_TITLE_LIMIT,
      value: reviewTitle,
      renderInput: ({
        count,
        value,
        onChange
      }) => /*#__PURE__*/_jsx(UITextInput, {
        "data-test-id": `${ReviewQuestions.TITLE}-wizard-input`,
        value: value,
        onChange: e => {
          onChange(e);
          onAnswerChange(ReviewQuestions.TITLE, e.target.value);
        },
        error: count < 0,
        placeholder: () => unescapedText(`marketplace-ui-reviews.wizard.profile.rate.placeholders.${ReviewQuestions.TITLE}`)
      })
    })
  }), [reviewTitle, onAnswerChange]);
  const reviewBody = content.get(ReviewQuestions.REVIEW);
  const reviewField = useMemo(() => /*#__PURE__*/_jsx(UIFormControl, {
    className: "m-top-1",
    required: true,
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `marketplace-ui-reviews.wizard.profile.rate.labels.${ReviewQuestions.REVIEW}`
    }),
    children: /*#__PURE__*/_jsx(UICharacterCounter, {
      label: ({
        count
      }) => /*#__PURE__*/_jsx("span", {
        style: {
          color: count && (count < REVIEW_BODY_MIN || count > REVIEW_BODY_MAX) ? CANDY_APPLE : EERIE
        },
        children: count < REVIEW_BODY_MIN ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-reviews.wizard.profile.rate.labels.minimum",
          options: {
            limit: REVIEW_BODY_MIN
          }
        }) : REVIEW_BODY_MAX - count
      }),
      value: reviewBody,
      renderInput: ({
        count,
        value,
        onChange
      }) => /*#__PURE__*/_jsx(UITextArea, {
        "data-test-id": `${ReviewQuestions.REVIEW}-wizard-input`,
        rows: 4,
        value: value,
        onChange: e => {
          onChange(e);
          onAnswerChange(ReviewQuestions.REVIEW, e.target.value);
        },
        error: !!count && (count < REVIEW_BODY_MIN || count > REVIEW_BODY_MAX),
        placeholder: unescapedText(`marketplace-ui-reviews.wizard.profile.rate.placeholders.${ReviewQuestions.REVIEW}`, {
          companyName
        })
      })
    })
  }), [reviewBody, onAnswerChange, companyName]);
  return /*#__PURE__*/_jsxs(ReviewWizardGrid, {
    children: [/*#__PURE__*/_jsx(H4, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-ui-reviews.wizard.profile.rate.header",
        options: {
          companyName
        }
      })
    }), /*#__PURE__*/_jsxs(UIForm, {
      children: [ratingField, servicesSelect, titleField, reviewField]
    })]
  });
}